/* ---------------------------------------------------------------------------
   Construction Scheduler: one structure's programme.

   The app's only working screen. You arrive at it from the register beside it —
   a row opens the programme here, where the same row opens the record in Bridge
   Inspection (see `structure` in src/core/apps.ts) — or from a link that names
   the bridge, `scheduler.html#/schedule/BR-CHT-107`.

   TWO VIEWS, ONE AT A TIME, EACH WITH THE WHOLE PAGE. The ribbon offers the
   Gantt of the frozen baseline and the table of every planned activity against
   what the site has recorded (`scheduleView` in the store), and whichever is in
   front fills the content area under the strip of links to the same structure
   in the other applications — no page head, no figures, no notes. Both are
   drawn by src/screens/programme.ts, which also draws section (h) of the
   construction report; nothing is reimplemented here, and the variance logic is
   src/data/variance.ts and is checked there.

   EITHER VIEW CAN BE A CALENDAR instead — the switch in the panel's head
   (`scheduleLayout`), drawn by src/screens/programmeCalendar.ts.

   WHY IT IS A SCREEN AT ALL, given the report already shows the programme.
   Because a planner has no business opening a period report to reach a
   programme. Same data, different job — which is the whole argument for
   splitting the site into applications in the first place.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import { type State } from '../core/store.ts';
import { emptyState } from '../components/widgets.ts';
import { findBridge } from '../data/derive.ts';
import {
  programmeActivities,
  programmeActivitiesCalendar,
  programmeGantt,
  programmeGanttCalendar,
} from './programme.ts';

export function scheduleScreen(s: Readonly<State>): HTMLElement {
  const b = findBridge(s.selectedId);
  /* Null only where the register itself is empty — the same answer the bridge
     record gives, in the same words, because it is the same fact about the same
     register rather than anything about programmes. */
  if (b === null) return emptyState('This register has no structures in it yet.');

  const calendar = s.scheduleLayout === 'calendar';
  return h(
    'div',
    { class: 'schedfill' },
    s.scheduleView === 'gantt'
      ? (calendar ? programmeGanttCalendar : programmeGantt)(b.id, b.name, s)
      : (calendar ? programmeActivitiesCalendar : programmeActivities)(b.id, b.name, s),
  );
}
